import { getSection } from '../utils';
import { formatCurrencyFr, formatNumberFr, formatPercentageFr } from '@/lib/formatters';
import MarkdownText from '../MarkdownText';

function ContextItem({ label, value }) {
  return (
    <div data-testid={`context-${label}`}>
      <div className="text-[9px] text-cabinet-ink-secondary uppercase tracking-[0.22em] mb-1.5">{label}</div>
      <div className="font-playfair text-[15px] text-cabinet-ink leading-snug">{value || '—'}</div>
    </div>
  );
}

/**
 * Section I — Contexte officinal.
 * Grille 4 colonnes × 2 lignes = 8 informations contextuelles, telles que
 * définies dans le brief produit. Les valeurs viennent en priorité de
 * `section.content_data` (produit par Claude) avec fallback sur les
 * métadonnées `establishment` + `kpi_results` du rapport.
 */
export default function ContextSection({ report }) {
  const section = getSection(report, 'context');
  const data = section?.content_data || {};

  // Fallback values from kpi_results (CA mois → annualisé approximatif)
  const ca_mois = (report.kpi_results || []).find((k) => k.kpi_id === 'ca_ht')?.value;
  const ca_annuel_fallback = ca_mois ? ca_mois * 12 : null;

  const items = [
    { label: 'CA annuel estimé', value: data.ca_annuel ? formatCurrencyFr(data.ca_annuel) : (ca_annuel_fallback ? formatCurrencyFr(ca_annuel_fallback) : null) },
    { label: 'Surface de vente', value: data.surface_vente_m2 ? `${formatNumberFr(data.surface_vente_m2)} m²` : null },
    { label: 'Effectif ETP', value: data.etp ? formatNumberFr(data.etp, 1) : null },
    { label: 'Patientèle estimée', value: data.patientele_estimee ? formatNumberFr(data.patientele_estimee) : null },
    { label: 'Zone de chalandise', value: data.zone_chalandise || null },
    { label: 'Densité concurrentielle', value: data.densite_concurrentielle || null },
    { label: "Mix d'activité", value: data.mix_activite ? (typeof data.mix_activite === 'string' ? data.mix_activite : `${formatPercentageFr(data.mix_activite.ordonnance)} ordo / ${formatPercentageFr(data.mix_activite.libre)} libre`) : null },
    { label: 'Segment benchmark', value: data.segment_benchmark || report.cohort_label },
  ];

  return (
    <section data-testid="context-section">
      <div className="text-[10px] text-cabinet-bordeaux uppercase tracking-[0.3em] font-medium mb-2">
        I — Contexte officinal
      </div>
      <h2 className="font-playfair text-[22px] text-cabinet-ink mb-3">
        Rappel du contexte de votre officine
      </h2>
      <p className="font-playfair italic text-[14px] text-cabinet-ink-secondary mb-8 max-w-2xl">
        Avant de plonger dans les indicateurs, replaçons votre activité dans son écosystème.
      </p>

      <div className="bg-cabinet-paper border border-cabinet-border shadow-sm p-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-7">
          {items.map((item) => (
            <ContextItem key={item.label} label={item.label} value={item.value} />
          ))}
        </div>

        {section?.content_md && (
          <div className="mt-8 pt-6 border-t border-cabinet-border">
            <MarkdownText text={section.content_md} className="text-[13px] text-cabinet-ink-secondary leading-[1.7]" />
          </div>
        )}
      </div>
    </section>
  );
}
